/**
 * Super Admin — Settings:
 *  /superadmin/settings/flags    Feature flags
 *  /superadmin/settings/costs    Cost thresholds
 *  /superadmin/settings/admins   Admin accounts
 *  /superadmin/settings/audit    Audit log
 */
import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldOff, UserPlus } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";
import { SectionHeader } from "./SuperadminApp";
import { Card, TableShell, Th, Td, Modal, PrimaryBtn, GhostBtn, DangerBtn, inputStyle, fieldLabel, EmptyState, FilterPills, relTime, StatusBadge } from "./_atoms";

// ───────────────────────────────────────────────────────────────────
export function FeatureFlags() {
  const [flags, setFlags] = useState([]);
  const load = useCallback(() => {
    apiClient.get("/superadmin/flags").then((r) => setFlags(r.data?.flags || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, enabled) => {
    try {
      if (key === "maintenance_mode" && enabled) {
        if (!window.confirm("Enabling maintenance mode will hide the app for all non-admin users. Proceed?")) return;
      }
      await apiClient.post(`/superadmin/flags/${key}/toggle`, { enabled });
      toast.success(enabled ? "Enabled" : "Disabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="feature-flags">
      <SectionHeader title="Feature Flags" onRefresh={load} />
      <div style={{ display: "grid", gap: 10 }}>
        {flags.map((f) => (
          <Card key={f.key} data-testid={`flag-${f.key}`} style={f.key === "maintenance_mode" ? { borderLeft: "3px solid var(--error, #EF4444)" } : {}}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                <strong style={{ fontSize: 15 }}>{f.label}</strong>
                <div style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)" }}>{f.description}</div>
              </div>
              <Switch checked={f.enabled} onChange={(v) => toggle(f.key, v)} testid={`switch-${f.key}`} />
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Switch({ checked, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!checked)} role="switch" aria-checked={checked}
      data-testid={testid}
      style={{
        width: 44, height: 24, borderRadius: 100, position: "relative",
        background: checked ? "var(--success, #22C55E)" : "rgba(12,12,11,0.16)",
        transition: "background 180ms ease",
        border: "none",
      }}
    >
      <span style={{ position: "absolute", top: 2, left: checked ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 180ms ease" }} />
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────
export function CostThresholds() {
  const [t, setT] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    apiClient.get("/superadmin/settings/cost-thresholds").then((r) => setT(r.data || {}));
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(
        Object.entries(t).map(([k, v]) => [k, k === "alert_email" ? v : (parseInt(v, 10) || 0)])
      );
      await apiClient.post("/superadmin/settings/cost-thresholds", body);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const F = (k, label) => (
    <div><label style={fieldLabel}>{label} (₹)</label>
      <input style={inputStyle} value={t[k] ?? ""} onChange={(e) => setT({ ...t, [k]: e.target.value })} data-testid={`thr-${k}`} />
    </div>
  );
  return (
    <div className="space-y-4" data-testid="cost-thresholds">
      <SectionHeader title="Cost Thresholds" onRefresh={load} />
      <Card>
        <p style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)", marginBottom: 12 }}>
          When monthly spend on a service crosses 80% of its budget, the cost monitor highlights it. Alert email receives an email at 80% and 100%.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {F("claude_daily", "Claude — daily")}
          {F("claude_monthly", "Claude — monthly")}
          {F("gemini_daily", "Gemini — daily")}
          {F("gemini_monthly", "Gemini — monthly")}
          {F("external_api_daily", "External APIs — daily")}
          {F("s3_monthly", "S3 — monthly")}
          {F("usd_to_inr", "USD→INR")}
          <div><label style={fieldLabel}>Alert email</label>
            <input style={inputStyle} value={t.alert_email || ""} onChange={(e) => setT({ ...t, alert_email: e.target.value })} placeholder="ops@fitfrik.in" />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <PrimaryBtn onClick={save} disabled={busy} data-testid="thr-save">{busy ? "Saving…" : "Save"}</PrimaryBtn>
        </div>
      </Card>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
export function AdminAccounts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    apiClient.get("/superadmin/admins").then((r) => setRows(r.data?.admins || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const promote = async (uid, role) => {
    try {
      await apiClient.post(`/superadmin/admins/${uid}/promote`, { role });
      toast.success("Promoted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revoke = async (uid) => {
    if (!window.confirm("Revoke admin access for this user?")) return;
    try {
      await apiClient.post(`/superadmin/admins/${uid}/revoke`);
      toast.success("Revoked"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-accounts">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <SectionHeader title="Admin Accounts" onRefresh={load} />
        <PrimaryBtn onClick={() => setOpen(true)} data-testid="promote-admin"><UserPlus size={14} /> Promote User</PrimaryBtn>
      </div>
      {rows.length === 0 ? <EmptyState title="No admin accounts" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>2FA</Th><Th>Last active</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.user_id}>
                  <Td><strong>{u.name || "—"}</strong></Td>
                  <Td style={{ fontSize: 12 }}>{u.email}</Td>
                  <Td>
                    {u.is_superadmin ? (
                      <span style={{ background: "var(--brand-gold, #F5C518)", color: "#0C0C0B", padding: "3px 10px", borderRadius: 100, fontSize: 11, fontWeight: 700 }}>SUPERADMIN</span>
                    ) : <StatusBadge value="admin" />}
                  </Td>
                  <Td>{u.totp_enabled_at ? <ShieldCheck size={14} style={{ color: "var(--success, #22C55E)" }} /> : <ShieldOff size={14} style={{ color: "var(--text-tertiary, #8A8884)" }} />}</Td>
                  <Td>{relTime(u.last_active_at)}</Td>
                  <Td>
                    <div style={{ display: "flex", gap: 6 }}>
                      {!u.is_superadmin && (
                        <GhostBtn
                          onClick={() => promote(u.user_id, "superadmin")}
                          style={{ height: 30, padding: "0 10px", fontSize: 11 }}
                          data-testid={`promote-super-${u.user_id}`}
                        >Promote → Superadmin</GhostBtn>
                      )}
                      <DangerBtn
                        onClick={() => revoke(u.user_id)}
                        style={{ height: 30, padding: "0 10px", fontSize: 11 }}
                        data-testid={`revoke-${u.user_id}`}
                      >Revoke</DangerBtn>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
      {open && <PromoteModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function PromoteModal({ onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("admin");
  const submit = async () => {
    if (!email) return toast.error("Email required");
    try {
      const { data } = await apiClient.get("/superadmin/users", { params: { q: email, size: 5 } });
      const match = (data?.users || []).find((u) => u.email?.toLowerCase() === email.toLowerCase());
      if (!match) return toast.error("User not found — they must register first");
      await apiClient.post(`/superadmin/admins/${match.user_id}/promote`, { role });
      toast.success("Promoted"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal open onClose={onClose} title="Promote User to Admin">
      <div style={{ display: "grid", gap: 10 }}>
        <div><label style={fieldLabel}>User email (must be registered)</label><input style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="promote-email" /></div>
        <div><label style={fieldLabel}>Role</label>
          <select style={inputStyle} value={role} onChange={(e) => setRole(e.target.value)} data-testid="promote-role">
            <option value="admin">Admin</option>
            <option value="superadmin">Superadmin</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={submit} data-testid="promote-submit">Promote</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

// ───────────────────────────────────────────────────────────────────
export function AuditLog() {
  const [rows, setRows] = useState([]);
  const [actionType, setActionType] = useState("");
  const load = useCallback(() => {
    apiClient.get("/superadmin/settings/audit-log", { params: { limit: 200, action_type: actionType || undefined } })
      .then((r) => setRows(r.data || []));
  }, [actionType]);
  useEffect(() => { load(); }, [load]);

  const types = Array.from(new Set(rows.map((r) => r.action_type))).sort();
  return (
    <div className="space-y-4" data-testid="audit-log">
      <SectionHeader title="Audit Log" onRefresh={load} />
      <Card>
        <label style={fieldLabel}>Filter by action type</label>
        <select style={inputStyle} value={actionType} onChange={(e) => setActionType(e.target.value)} data-testid="audit-filter">
          <option value="">All actions</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Card>
      {rows.length === 0 ? <EmptyState title="No audit events" /> : (
        <TableShell>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><Th>When</Th><Th>Admin</Th><Th>Action</Th><Th>Target</Th><Th>Detail</Th><Th>IP</Th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i}>
                  <Td style={{ fontSize: 12 }}>{relTime(r.created_at)}</Td>
                  <Td style={{ fontSize: 11, fontFamily: "monospace" }}>{(r.admin_user_id || "").slice(0, 12)}</Td>
                  <Td><StatusBadge value={r.action_type} /></Td>
                  <Td style={{ fontSize: 12 }}>{r.target_type}: {r.target_id?.slice(0, 14)}</Td>
                  <Td style={{ fontSize: 11, fontFamily: "monospace", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>{JSON.stringify(r.action_detail || {}).slice(0, 80)}</Td>
                  <Td style={{ fontSize: 11 }}>{r.ip_address || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// Runtime config diagnostics — effective values the backend is USING right
// now. Redacted (prefixes + booleans for anything sensitive). Every open of
// this panel writes to the Super Admin audit log.
export function ConfigDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("/superadmin/config/diagnostics")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const row = (label, value, testid, warn) => (
    <div data-testid={testid} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--border, rgba(12,12,11,0.06))", gap: 12 }}>
      <span style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: warn ? "var(--error, #EF4444)" : "var(--text-primary, #0C0C0B)", fontFamily: "monospace", textAlign: "right" }}>{value}</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="config-diagnostics">
      <SectionHeader title="Config Diagnostics" onRefresh={load} />
      <Card>
        <p style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)", marginBottom: 12 }}>
          Effective runtime values the backend is using RIGHT NOW. Every open writes an audit row.
          Secrets are redacted — only prefixes and booleans are shown.
        </p>
        {loading && <div style={{ padding: 12, textAlign: "center", color: "var(--text-tertiary, #8A8884)" }}>Loading…</div>}
        {data && (
          <div>
            {row("Environment", data.environment, "diag-env")}
            {row("Tracking started at", data.tracking_started_at || "(none)", "diag-tracking")}
            {(() => {
              const b = data.broadcast_admin_daily_limit || {};
              const warn = b.env_raw != null && Number(b.env_raw) > b.hard_cap;
              return row(
                "Broadcast admin/day",
                `${b.effective} · source=${b.source} · code_default=${b.code_default} · hard_cap=${b.hard_cap}${b.env_raw != null ? ` · env_raw=${b.env_raw}` : ""}${warn ? " · CLAMPED" : ""}`,
                "diag-broadcast",
                warn,
              );
            })()}
            {row("Razorpay mode", `${data.razorpay.mode.toUpperCase()} (${data.razorpay.key_prefix || "unset"})`, "diag-razorpay", data.razorpay.mode === "live" && data.environment !== "production")}
            {row("Razorpay secret", data.razorpay.secret_configured ? "configured" : "MISSING", "diag-razorpay-secret", !data.razorpay.secret_configured)}
            {row("Refresh cookie domain", data.refresh_cookie_domain, "diag-cookie-domain")}
            {row("CORS allow-list", `${data.cors.count} origin(s): ${data.cors.allow_list.join(", ")}`, "diag-cors")}
            {row("Sender email", data.email.sender, "diag-sender")}
            {row("Resend configured", data.email.resend_configured ? "yes" : "NO", "diag-resend", !data.email.resend_configured)}
            {(() => {
              const fs = data.food_scan || {};
              return row(
                "Food-scan model",
                `${fs.model || "unset"} · source=${fs.model_source || "?"}${fs.env_raw ? ` · env_raw=${fs.env_raw}` : ""}`,
                "diag-food-scan-model",
                false,
              );
            })()}
            {(() => {
              const s = (data.food_scan || {}).single || {};
              return row(
                "Food-scan p50/p95 (single)",
                s.count
                  ? `p50=${s.p50_ms}ms · p95=${s.p95_ms}ms · n=${s.count}`
                  : "no data yet",
                "diag-food-scan-single",
                s.p50_ms != null && s.p50_ms > 7000,
              );
            })()}
            {(() => {
              const m = (data.food_scan || {}).multi || {};
              return row(
                "Food-scan p50/p95 (multi)",
                m.count
                  ? `p50=${m.p50_ms}ms · p95=${m.p95_ms}ms · n=${m.count}`
                  : "no data yet",
                "diag-food-scan-multi",
                m.p50_ms != null && m.p50_ms > 7000,
              );
            })()}
          </div>
        )}
      </Card>
    </div>
  );
}
